import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { PostTaskBody } from '@snootsvet/shared'

import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useOsApi } from '@/frontend/hooks/api/use-os-api'

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never

/** Creates a task in the active region. */
export function useCreateTask() {
  const api = useOsApi()
  const queryClient = useQueryClient()
  const { region } = useViewerRegion()

  return useMutation({
    mutationFn: async (input: DistributiveOmit<PostTaskBody, 'region'>) => {
      const response = await api.post('/tasks', { body: { ...input, region } })
      if (!response.success) throw new Error(response.error.message)
      return response.data
    },
    onSuccess: (_result, body) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['patient-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['patient-timeline'] })
      // A newly created task may be assigned to the current user or their team,
      // so refresh the sidebar "my tasks" badge.
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
      // A procedure request stamps the patient (dental sets dentalRequestedAt).
      if (body.patientId) {
        queryClient.invalidateQueries({ queryKey: ['patient-tasks', body.patientId] })
        queryClient.invalidateQueries({ queryKey: ['patient', body.patientId] })
      }
    },
  })
}
